'use client';

import { useRouter } from 'next/navigation';

import { FormattedMessage } from '@flipster/i18n';
import { AutomationIconography } from '@flipster/react/graphic-assets/iconography/AutomationIconography';
import { InterfaceIconography } from '@flipster/react/graphic-assets/iconography/InterfaceIconography';
import { SeamlessIconography } from '@flipster/react/graphic-assets/iconography/SeamlessIconography';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { css } from '@flipster/styled-system/css';
import { square, stack } from '@flipster/styled-system/patterns';
import { convertWebviewToWebUrl } from '@flipster/utils/env-helper';
import webviewHandler from '@flipster/utils/webview-handler';

import {
  CarouselScrollContainer,
  ScrollIndicator,
  scrollTrackStyles,
  useCarouselScroll,
} from '../../misc/carousel-scroll';
import { useProductConfig } from '../../product-config';

const defaultLearnMorePath = '/blog/dynamic-earn-a-simpler-way-to-access-on-chain-yield';

const defaultCards = [
  {
    icon: <AutomationIconography size={48} />,
    title: <FormattedMessage id='Earn.r260224.Advantages_item_1_title' />,
    desc: <FormattedMessage id='Earn.r260224.Advantages_item_1_description' />,
  },
  {
    icon: <InterfaceIconography size={48} />,
    title: <FormattedMessage id='Earn.r260224.Advantages_item_2_title' />,
    desc: <FormattedMessage id='Earn.r260224.Advantages_item_2_description' />,
  },
  {
    icon: <SeamlessIconography size={48} />,
    title: <FormattedMessage id='Earn.r260224.Advantages_item_3_title' />,
    desc: <FormattedMessage id='Earn.r260224.Advantages_item_3_description' />,
  },
];

export const Advantages = () => {
  const { advantageItems, learnMorePath } = useProductConfig();
  const cards = advantageItems ?? defaultCards;
  const router = useRouter();
  const { scrollRef, scrollProgress, handleScroll, handleProgressChange } = useCarouselScroll();
  const resolvedLearnMorePath = learnMorePath ?? defaultLearnMorePath;

  const handleLearnMore = () => {
    webviewHandler.postMessage(
      { action: 'external-link', params: { url: convertWebviewToWebUrl(resolvedLearnMorePath) } },
      () => router.push(resolvedLearnMorePath),
    );
  };

  return (
    <section className={stack({ tabletDown: { gap: 16 }, tablet: { gap: 24 } })}>
      <h2 className={css({ textStyle: 'Title/L', color: 'content.default.level-5' })}>
        <FormattedMessage id='Earn.r260224.Advantages_title' />
      </h2>

      <CarouselScrollContainer progress={scrollProgress}>
        <div ref={scrollRef} onScroll={handleScroll} className={css(scrollTrackStyles, { gap: 12 })}>
          {cards.map((card, idx) => (
            <div
              key={idx}
              className={stack({
                flexShrink: 0,
                w: 280,
                p: 24,
                gap: 16,
                rounded: 12,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default.level-1',
                scrollSnapAlign: 'start',
              })}
            >
              {card.icon}
              <div className={stack({ gap: 8 })}>
                <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>{card.title}</p>
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>{card.desc}</p>
              </div>
            </div>
          ))}

          <button
            type='button'
            onClick={handleLearnMore}
            className={square({
              alignSelf: 'center',
              size: 120,
              rounded: 12,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-1',
              transition: 'background 0.2s',
              _hover: { bg: 'background.default.level-1' },
            })}
          >
            <div className={stack({ align: 'center', gap: 8 })}>
              <ArrowRightIcon className={css({ w: 20, h: 20, color: 'content.default.level-5' })} />
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage id='Common.r250401.Learn_more' />
              </p>
            </div>
          </button>
        </div>
      </CarouselScrollContainer>

      <ScrollIndicator progress={scrollProgress} onProgressChange={handleProgressChange} />
    </section>
  );
};
